import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { C } from '../constants';
import Glass from '../components/Glass';
import { setMeta, setJsonLd, removeJsonLd } from '../utils/meta';

const accent = C.cyan;

const SECTIONS = [
  {
    label: 'General',
    faqs: [
      { q: 'What Is UnofficialElections.com?', a: 'UnofficialElections.com is a crowd-sourced straw poll for Indian elections. It is not a scientific survey, but a real-time pulse check on public sentiment. The platform is independent and not affiliated with any government body, political party, or media house.' },
      { q: 'Is This an Official Government Website?', a: 'No. UnofficialElections is an independent civic engagement project with no government, party, or media affiliation. It exists purely for entertainment, civic engagement, and information.' },
      { q: 'Which States Are Available?', a: 'Tamil Nadu and West Bengal are live for the 2026 assembly elections. Coverage expands to Uttar Pradesh, Punjab, Gujarat, Goa, Uttarakhand, Himachal Pradesh, and Manipur for 2027, and continues through the 2029 Lok Sabha polls.' },
      { q: 'Is UnofficialElections Available in Regional Languages?', a: 'Yes. Each state page includes bilingual text in English plus the state\'s primary language — Tamil for Tamil Nadu, Bengali for West Bengal, and so on for each future state.' },
      { q: 'Who Built This Platform?', a: 'An independent solo developer with no corporate backing and no political affiliation built UnofficialElections as a civic engagement side project.' },
    ],
  },
  {
    label: 'Voting',
    faqs: [
      { q: 'How Do I Vote?', a: 'Visit unofficialelections.com, select your state, and tap on your preferred party. No registration, no login, no phone number, no email. Takes about ten seconds.' },
      { q: 'Can I Vote More Than Once?', a: 'No. The system allows one vote per device per state. Your choice is stored in your browser\'s local storage to prevent repeat voting — one of the platform\'s key anti-manipulation controls.' },
      { q: 'Can I Change My Vote After Casting It?', a: 'No. Votes are final once submitted. This prevents repeated switching that could distort results and keeps the data consistent.' },
      { q: 'Can I Vote for Different Parties in Different States?', a: 'Yes. If multiple states are live, you can cast one vote per state. Each state operates as an independent poll.' },
      { q: 'Does My Vote Here Affect the Actual Election?', a: 'No. This is an informal, unofficial poll for civic engagement only. It has no connection to the official electoral process. Always exercise your real vote through the official Election Commission process.' },
      { q: 'How Are the Listed Parties Decided?', a: 'The platform lists major parties registered with the Election Commission that hold a meaningful presence in the state\'s political arena. Every listed party receives equal placement.' },
    ],
  },
  {
    label: 'Privacy & Data',
    faqs: [
      { q: 'Is My Vote Anonymous?', a: 'Yes, completely. No name, email, phone number, IP address, or any identifying information is collected. The platform has no way of knowing who cast any individual vote.' },
      { q: 'What Data Does UnofficialElections Collect?', a: 'Only aggregate vote counts per district and party. No individual-level data is collected or stored on the server. For example: "Chennai has 50 votes for Party A and 30 for Party B." No vote can be traced back to an individual.' },
      { q: 'Where Is My Vote Stored?', a: 'Your individual choice stays on your device in browser local storage. The server receives only the updated aggregate count for the district and party you selected, with no identifying information attached.' },
      { q: 'Can Anyone See Who I Voted For?', a: 'No. Not the platform, not other users, not anyone. The only scenario where your choice could be seen is if someone physically accessed your device\'s browser storage.' },
      { q: 'Does UnofficialElections Sell Data?', a: 'No. There is no personal data to sell. Aggregate results are already public on the site — that transparency is the point.' },
    ],
  },
  {
    label: 'Results & Data',
    faqs: [
      { q: 'How Accurate Are the Results?', a: 'This is a crowd-sourced straw poll, not a scientific survey. Results reflect the preferences of site visitors and skew toward younger, urban, internet-connected voters. The data should not be interpreted as a prediction of actual election outcomes.' },
      { q: 'How Often Do the Results Update?', a: 'Results update in real time. As soon as someone votes, the statewide totals, district map, and district breakdowns all reflect the new data instantly.' },
      { q: 'What Is the District Map?', a: 'A stylised tile grid representing each district in the state, colour-coded by leading party, giving every district equal visual weight — unlike a geographic map where smaller districts become invisible.' },
      { q: 'Can I See Results for a Specific District?', a: 'Yes. Tap any district tile on the results page to see a full breakdown of all parties\' vote counts in that district.' },
      { q: 'Why Do Some Districts Show Zero Votes?', a: 'Some districts may not have received any votes yet, especially early on. Sharing the platform with people from underrepresented districts is the fastest way to fill in the map.' },
    ],
  },
  {
    label: 'Platform & Future',
    faqs: [
      { q: 'Does the Site Work on Mobile?', a: 'Yes. The site is optimised for mobile devices and works on all modern smartphones and browsers. It loads fast, uses minimal data, and works on budget smartphones.' },
      { q: 'Will UnofficialElections Add Lok Sabha Elections?', a: 'Yes. The platform plans to cover the 2029 Lok Sabha general elections with constituency-level data across India.' },
      { q: 'How Can I Help the Platform Grow?', a: 'Share the link on WhatsApp, Instagram, X, Telegram, or any platform where political discussion happens. The more people who vote, the more meaningful and representative the data becomes.' },
      { q: 'Can Journalists Use This Data?', a: 'Yes, with appropriate attribution. Credit UnofficialElections.com and note that the data is crowd-sourced and unofficial. Reach out directly for additional context.' },
      { q: 'Which States Are Coming in 2027?', a: 'Uttar Pradesh, Punjab, Gujarat, Goa, Uttarakhand, Himachal Pradesh, and Manipur. Each will be added as its election timeline approaches with full district maps, all contesting parties, and real-time results.' },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: `1px solid ${C.border}`,
        cursor: 'pointer',
      }}
      onClick={() => setOpen(o => !o)}
    >
      <div className="flex items-center justify-between gap-4" style={{ padding: '1rem 0' }}>
        <span style={{ fontWeight: 600, fontSize: '0.95rem', color: C.white, lineHeight: 1.4 }}>{q}</span>
        <ChevronDown
          size={18} color={C.textDim}
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}
        />
      </div>
      {open && (
        <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, marginBottom: '1rem', marginTop: 0 }}>
          {a}
        </p>
      )}
    </div>
  );
}

export default function FAQScreen() {
  useEffect(() => {
    setMeta({
      title:       'FAQ - Frequently Asked Questions | UnofficialElections',
      description: 'Answers to common questions about UnofficialElections — how voting works, privacy, accuracy, available states, and future plans.',
      ogImage:     '/og-home.png',
      canonical:   '/faq',
    });
    setJsonLd('faq-page', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'What is UnofficialElections.com?',
          acceptedAnswer: { '@type': 'Answer', text: "UnofficialElections.com is a free, anonymous platform where Indian citizens can cast an informal vote in state elections and see live, district-level results on an interactive map." } },
        { '@type': 'Question', name: 'Is my vote anonymous?',
          acceptedAnswer: { '@type': 'Answer', text: "Yes, completely. We don't collect your name, email, phone number, or any identifying information. Your vote choice is stored only on your device." } },
        { '@type': 'Question', name: 'Which states are available on UnofficialElections?',
          acceptedAnswer: { '@type': 'Answer', text: "Tamil Nadu and West Bengal are live. Uttar Pradesh, Punjab, Gujarat, Goa, Uttarakhand, Himachal Pradesh, and Manipur are coming for 2027 elections." } },
        { '@type': 'Question', name: 'Is this an official election poll?',
          acceptedAnswer: { '@type': 'Answer', text: "No. UnofficialElections is not affiliated with the Election Commission of India or any political party. It is a crowd-sourced platform for entertainment and civic engagement." } },
        { '@type': 'Question', name: 'Can I vote more than once?',
          acceptedAnswer: { '@type': 'Answer', text: "No. The system allows one vote per device per state to maintain data integrity." } },
      ],
    });
    return () => removeJsonLd('faq-page');
  }, []);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Help & Support
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        Frequently Asked Questions
      </h1>
      <p style={{ fontSize: '0.95rem', color: C.textDim, marginTop: '0.6rem', maxWidth: 560, lineHeight: 1.6 }}>
        Everything you need to know about how UnofficialElections works.
      </p>

      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap mt-6 mb-8">
        {SECTIONS.map((sec, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            style={{
              padding: '6px 16px', borderRadius: 999, fontSize: '0.78rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
              border: `1px solid ${activeTab === i ? accent : C.border}`,
              background: activeTab === i ? 'rgba(0,229,255,0.10)' : 'transparent',
              color: activeTab === i ? accent : C.textDim,
              transition: 'all 150ms ease',
            }}
          >
            {sec.label}
          </button>
        ))}
      </div>

      {/* FAQ list */}
      <Glass style={{ padding: '0 1.4rem' }}>
        {SECTIONS[activeTab].faqs.map((item, i) => (
          <FAQItem key={i} q={item.q} a={item.a} />
        ))}
      </Glass>

      <Glass style={{ marginTop: '2.5rem', padding: '1rem 1.3rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.textDim, margin: 0, lineHeight: 1.6 }}>
          UnofficialElections.com is unofficial, non-political, and for entertainment and civic engagement only. Always exercise your real vote through the Election Commission of India's official process.
        </p>
      </Glass>
    </main>
  );
}
